import { z } from 'zod';

import { isoDaySchema } from './clinical-common.ts';
import { codeSystemKeySchema } from './terminology.ts';

// The import framework (docs/plans/phase1-terminology-and-import.md §4): start, upload in
// chunks, validate every row, preview problems, commit, report. Kinds plug in; terminology
// is the first. Patients, price lists and opening stock arrive with their slices.

export const IMPORT_KINDS = [
  'terminology',
  'patients',
  'price_list',
  'opening_stock',
  'sha_preauth_list',
] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];

export const IMPORT_STATUSES = [
  'uploading',
  'uploaded',
  'validating',
  'validated',
  'committing',
  'committed',
  'failed',
  'cancelled',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

/** Statuses from which nothing more happens. */
export const FINISHED_IMPORT_STATUSES = ['committed', 'failed', 'cancelled'] as const;

/** Upload chunk size. The last chunk may be smaller. */
export const IMPORT_CHUNK_BYTES = 1024 * 1024;

/** Limits per kind (plan §12, decision 8). */
export const IMPORT_LIMITS = {
  terminology: { maxBytes: 200 * 1024 * 1024, maxRows: 1_000_000 },
  // docs/plans/phase1-importers.md §13, decision 12.
  patients: { maxBytes: 20 * 1024 * 1024, maxRows: 100_000 },
  price_list: { maxBytes: 20 * 1024 * 1024, maxRows: 20_000 },
  opening_stock: { maxBytes: 20 * 1024 * 1024, maxRows: 5_000 },
  // The SHA pre-authorisation list (docs/plans/phase1-sha.md §4.6): a few thousand rows.
  sha_preauth_list: { maxBytes: 5 * 1024 * 1024, maxRows: 100_000 },
} as const satisfies Record<ImportKind, { maxBytes: number; maxRows: number }>;

/** Any kind other than terminology gets these when it is added. */
export const DEFAULT_IMPORT_LIMITS = { maxBytes: 20 * 1024 * 1024, maxRows: 1_000_000 } as const;

export const IMPORT_FORMATS = {
  terminology: ['wodi-terminology-csv-v1'],
  patients: ['wodi-patients-csv-v1'],
  price_list: ['wodi-price-list-csv-v1'],
  opening_stock: ['wodi-opening-stock-csv-v1'],
  sha_preauth_list: ['wodi-sha-preauth-list-csv-v1'],
} as const satisfies Record<ImportKind, readonly string[]>;

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'Must be a lowercase hex SHA-256');

export const terminologyImportParametersSchema = z.object({
  codeSystemKey: codeSystemKeySchema,
  version: z.string().trim().min(1).max(64),
  releaseDate: isoDaySchema.nullable(),
});

export type TerminologyImportParameters = z.infer<typeof terminologyImportParametersSchema>;

/** Patients are registered at the session's facility; the import takes no parameters. */
export const patientImportParametersSchema = z.object({}).strict();

export type PatientImportParameters = z.infer<typeof patientImportParametersSchema>;

/**
 * Prices go on one list, starting "now" or at 00:00 EAT on a later day, as "Change price"
 * does (docs/plans/phase1-importers.md §4.1). A row's own `starts_on` overrides the day.
 */
export const priceListImportParametersSchema = z.object({
  priceListId: z.uuid(),
  startsOn: z.union([z.literal('now'), isoDaySchema]),
  /** Create services whose code isn't in the catalogue yet; needs catalogue.manage. */
  createMissingServices: z.boolean().default(false),
});

export type PriceListImportParameters = z.infer<typeof priceListImportParametersSchema>;

/**
 * Opening balances for one store at the session's facility (docs/plans/phase1-importers.md
 * §5.1). `countedAt` becomes each document's `occurredAt`; it can't be in the future.
 */
export const openingStockImportParametersSchema = z.object({
  storeId: z.uuid(),
  countedAt: z.iso.datetime({ offset: true }),
  /** Kept on every document the import requests, before "part 1 of 2". */
  note: z.string().trim().max(400).nullable().default(null),
});

export type OpeningStockImportParameters = z.infer<typeof openingStockImportParametersSchema>;
/** Where the list came from: the official file's name and release (plan §4.6). */
export const preauthListImportParametersSchema = z.object({
  sourceReference: z.string().trim().min(3).max(200),
});

export type PreauthListImportParameters = z.infer<typeof preauthListImportParametersSchema>;

const fileFields = {
  fileName: z.string().trim().min(1).max(255),
  fileSizeBytes: z.number().int().min(1),
  fileSha256: sha256Schema,
  clientRequestId: z.uuid(),
};

export const startImportBodySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('terminology'),
    format: z.enum(IMPORT_FORMATS.terminology),
    parameters: terminologyImportParametersSchema,
    ...fileFields,
  }),
  z.object({
    kind: z.literal('patients'),
    format: z.enum(IMPORT_FORMATS.patients),
    parameters: patientImportParametersSchema,
    ...fileFields,
  }),
  z.object({
    kind: z.literal('price_list'),
    format: z.enum(IMPORT_FORMATS.price_list),
    parameters: priceListImportParametersSchema,
    ...fileFields,
  }),
  z.object({
    kind: z.literal('opening_stock'),
    format: z.enum(IMPORT_FORMATS.opening_stock),
    parameters: openingStockImportParametersSchema,
    ...fileFields,
  }),
  z.object({
    kind: z.literal('sha_preauth_list'),
    format: z.enum(IMPORT_FORMATS.sha_preauth_list),
    parameters: preauthListImportParametersSchema,
    ...fileFields,
  }),
]);

export type StartImportBody = z.infer<typeof startImportBodySchema>;

export const importIssueGroupSchema = z.object({
  code: z.string(),
  severity: z.enum(['error', 'warning']),
  count: z.number().int(),
  message: z.string(),
  examples: z.array(
    z.object({
      rowNumber: z.number().int(),
      line: z.number().int(),
      column: z.string().nullable(),
    }),
  ),
});

export const importJobSchema = z.object({
  id: z.uuid(),
  kind: z.enum(IMPORT_KINDS),
  format: z.string(),
  status: z.enum(IMPORT_STATUSES),
  /** Null when the viewer may not see the file name (it can itself be personal data). */
  fileName: z.string().nullable(),
  fileSizeBytes: z.number().int(),
  fileSha256: z.string(),
  chunkCount: z.number().int(),
  receivedChunks: z.array(z.number().int()),
  totalRows: z.number().int(),
  validRows: z.number().int(),
  warningRows: z.number().int(),
  invalidRows: z.number().int(),
  validatedThroughRow: z.number().int(),
  committedThroughRow: z.number().int(),
  /** A file-level problem (such as an unclosed quote), with the line it is on. */
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  errorLine: z.number().int().nullable(),
  /** Header problems and unknown columns, shown above the row issues. */
  headerIssues: z.array(
    z.object({
      code: z.string(),
      column: z.string(),
      position: z.number().int().nullable(),
      message: z.string(),
    }),
  ),
  issueGroups: z.array(importIssueGroupSchema),
  /**
   * What committing would do, by outcome, from the preview (for prices: newPrices,
   * changedPrices, unchanged...). Empty for kinds that don't count outcomes.
   */
  previewCounts: z.record(z.string(), z.number().int()),
  /** What the commit created, by record type (for terminology: concepts and designations). */
  resultCounts: z.record(z.string(), z.number().int()),
  /**
   * Whether committing needs a reason, because some rows have warnings the kind asks
   * the person to acknowledge (possible duplicate patients), and the reason given.
   */
  acknowledgementRequired: z.boolean(),
  acknowledgementReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  createdBy: z.uuid(),
  validatedAt: z.iso.datetime().nullable(),
  committedAt: z.iso.datetime().nullable(),
});

export type ImportJobView = z.infer<typeof importJobSchema>;

export const importJobSummarySchema = importJobSchema.pick({
  id: true,
  kind: true,
  format: true,
  status: true,
  fileName: true,
  totalRows: true,
  validRows: true,
  warningRows: true,
  invalidRows: true,
  createdAt: true,
  createdBy: true,
  committedAt: true,
});

export const importIssueSchema = z.object({
  rowNumber: z.number().int(),
  line: z.number().int(),
  column: z.string().nullable(),
  code: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
});

export const importIssuePageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  /** Continue after this row number. */
  afterRow: z.coerce.number().int().min(0).default(0),
});

export const importIssuePageSchema = z.object({
  items: z.array(importIssueSchema),
  nextAfterRow: z.number().int().nullable(),
});

export const commitImportBodySchema = z.object({
  /** The count the person saw in the preview, so a stale preview can't be committed. */
  expectedValidRows: z.number().int().min(1),
  /** Required when the preview says `acknowledgementRequired`. */
  acknowledgement: z
    .object({ reason: z.string().trim().min(5).max(500) })
    .nullable()
    .default(null),
});

export const cancelImportBodySchema = z.object({
  reason: z.string().trim().min(5).max(500),
});

export const importColumnSchema = z.object({
  key: z.string(),
  required: z.boolean(),
  description: z.string(),
});

export const importKindViewSchema = z.object({
  kind: z.enum(IMPORT_KINDS),
  label: z.string(),
  maxBytes: z.number().int(),
  maxRows: z.number().int(),
  formats: z.array(
    z.object({ format: z.string(), label: z.string(), columns: z.array(importColumnSchema) }),
  ),
});

export const importKindListSchema = z.object({ items: z.array(importKindViewSchema) });
